import { IoSearch, IoClose, IoArrowBack } from "react-icons/io5";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import { SEARCH } from "../../constants/RoutesUrl";
import { debounce } from "lodash";
import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Language } from "../../App";
import RecentSearchIcon from "../../assets/images/icons/recent-search.svg";
import SearchIcon from "../../assets/images/icons/search.svg";
import "./SearchInput.css";
import {
  clearRecentSearches,
  getSearchAgainstKeywordApi,
} from "../../services/searchApis";
import { useMutation, useQuery } from "react-query";
import ClearSearchPopup from "./ClearSearchPopup";
import { toast } from "react-toastify";
import MixpanelManager from "../../constants/MixpanelManager";
import { MixpanelEvents } from "../../constants/MixpanelEvents";
import CustomTooltip from "../common/CustomTooltip";

export default function SearchInput({ topSearches = [], recentSearches = [], setRecentSearches }) {
  const { lang } = useContext(Language);
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [search, setSearch] = useState("");
  const [isClearing, setIsClearing] = useState(false);
  const inputRef = useRef(null);
  const textInputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const query = useLocation().search;
  const searchKeyword = decodeURIComponent(query.split("=")[1] || "");

  const togglePopup = () => setIsPopupOpen(!isPopupOpen);

  const mutation = useMutation(clearRecentSearches, {
    onMutate: () => {
      setIsClearing(true);
    },
    onSuccess: () => {
      setIsClearing(false);
      togglePopup();
      setRecentSearches([]);
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('keyword');
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      toast.success("Recent searches cleared successfully!");
    },
    onError: (error) => {
      console.error(error.message);
      alert("Failed to clear recent searches.");
    },
  });

  const handleClear = () => {
    mutation.mutate();
  };

  const { data: searchAgainstKeyword, isLoading } = useQuery({
    queryKey: ["search-against-keyword", search, inputValue],
    queryFn: () => getSearchAgainstKeywordApi(search ? search : ""),
    enabled: !!search || !!inputValue,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const keyword = searchKeyword && searchKeyword === "undefined" ? "" : searchKeyword;
    setInputValue(keyword);
    setSearch(keyword);
  }, [searchKeyword]);

  // Global Ctrl+K / Cmd+K shortcut to trigger search
  useEffect(() => {
    const handleGlobalKeydown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsActive((prev) => {
          if (!prev) {
            MixpanelManager.track(MixpanelEvents.globalSearch, {
              user_id: JSON.parse(localStorage.getItem("SignupData"))?.data?.id || "",
            });
          }
          return !prev;
        });
      }
    };
    window.addEventListener("keydown", handleGlobalKeydown);
    return () => window.removeEventListener("keydown", handleGlobalKeydown);
  }, []);

  // Click outside & Escape key listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setIsActive(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Auto focus input when active
  useEffect(() => {
    if (isActive && textInputRef.current) {
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 60);
    }
  }, [isActive]);

  const handleSearchClick = () => {
    if (inputValue) {
      setSearch(inputValue);
      setIsActive(false);
      navigate(SEARCH + "?keyword=" + encodeURIComponent(inputValue));
    }
  };

  const debouncedSetInputValue = useCallback(
    debounce((value) => {
      setSearch(value);
    }, 400),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetInputValue(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue) {
      setSearch(inputValue);
      setIsActive(false);
      navigate(SEARCH + "?keyword=" + encodeURIComponent(inputValue));
    }
  };

  const handleSuggestionClick = (value) => {
    setInputValue(value);
    setSearch(value);
    setIsActive(false);
    navigate(SEARCH + "?keyword=" + encodeURIComponent(value));
  };

  const toggleSearch = () => {
    if (!isActive) {
      MixpanelManager.track(MixpanelEvents.globalSearch, {
        user_id: JSON.parse(localStorage.getItem("SignupData"))?.data?.id || "",
      });
    }
    setIsActive(!isActive);
  };

  return (
    <div className={`mr-search-expandable-wrap ${isActive ? "mr-search--active-mobile" : ""}`} ref={inputRef}>
      {/* Sliding Search Input Capsule (Expands from right to left or full width on mobile) */}
      <div className={`mr-search-slide-container ${isActive ? "mr-search-slide--open" : ""}`}>
        <form onSubmit={handleSubmit} className="m-0 w-100 position-relative d-flex align-items-center">
          <button
            type="button"
            className="mr-search-mobile-back d-xl-none"
            onClick={() => setIsActive(false)}
            aria-label="Close search"
          >
            <IoArrowBack size={18} />
          </button>
          <div className="position-relative flex-grow-1">
            <input
              ref={textInputRef}
              type="text"
              placeholder={lang?.search_medical_radar || "Search Medical Radar"}
              value={inputValue}
              onChange={handleSearchChange}
              className="mr-search-gradient-input"
            />
            {inputValue ? (
              <IoClose
                size={16}
                className="mr-search-inline-clear cursor"
                onClick={() => {
                  setInputValue("");
                  setSearch("");
                  textInputRef.current?.focus();
                }}
              />
            ) : (
              <span className="mr-search-inline-kbd d-none d-md-inline-block">⌘K</span>
            )}
          </div>
        </form>

        {/* Original Search Dropdown */}
        {isActive && search === "" && (
          <div className="search-dropdown">
            {recentSearches?.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center px-3 pt-3">
                  <h6 className="fs-14 roboto-medium text-muted">
                    {lang?.your_recent_searches}:
                  </h6>
                  <span
                    className="fs-14 text-primary cursor roboto-medium"
                    onClick={() => setIsPopupOpen(true)}
                  >
                    {lang.clear}
                  </span>
                </div>
                <div>
                  {recentSearches?.slice(0, 5).map((value, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center gap-2 cursor manue-hover px-3 py-2"
                      onClick={() => handleSuggestionClick(value)}
                    >
                      <img src={RecentSearchIcon} alt="recent-search" width={20} />
                      <h5 className="mb-0 fs-14 roboto-bold">{value}</h5>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h6 className="px-3 pt-3 fs-14 roboto-medium text-muted">
                {lang?.popular_searches}
              </h6>
              <div>
                {topSearches?.slice(0, 5).map((value, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center gap-2 cursor px-3 py-2"
                    onClick={() => handleSuggestionClick(value)}
                  >
                    <img src={SearchIcon} alt="search" width={20} />
                    <h5 className="mb-0 fs-14 roboto-bold">{value}</h5>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {isActive && search && (
          <div className="search-dropdown">
            <div>
              {isLoading ? (
                <div className="w-100 p-3">
                  <h5 className="mb-0 fs-14 roboto-bold text-center text-muted">
                    Searching...
                  </h5>
                </div>
              ) : searchAgainstKeyword?.data?.length > 0 ? (
                <>
                  <div className="border-bottom">
                    {searchAgainstKeyword?.data?.slice(0, 10).map((value, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center gap-2 m-3 cursor"
                        onClick={() => handleSuggestionClick(value)}
                      >
                        <img src={SearchIcon} alt="search" width={20} />
                        <h5 className="mb-0 fs-14 roboto-bold">{value}</h5>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex justify-content-center">
                    <h5
                      className="fs-14 roboto-bold cursor my-3 text-primary"
                      onClick={handleSearchClick}
                    >
                      {lang?.see_all_results}
                    </h5>
                  </div>
                </>
              ) : (
                <div className="w-100 p-3">
                  <h5 className="mb-0 fs-14 roboto-bold text-center text-muted">
                    {lang.no_results_found}
                  </h5>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Search Trigger Button */}
      <CustomTooltip text={lang?.search ? `${lang.search} (Ctrl+K)` : "Search (Ctrl+K)"}>
        <div
          className={`mr-island-action-btn ${isActive ? "mr-island-action-btn--search-active" : ""}`}
          onClick={toggleSearch}
        >
          <FaMagnifyingGlass
            size={16.5}
            color={isActive ? "#00BADA" : "#0F172A"}
            style={{
              stroke: isActive ? "#00BADA" : "#0F172A",
              strokeWidth: "10px",
            }}
          />
        </div>
      </CustomTooltip>

      <ClearSearchPopup
        isClearing={isClearing}
        isOpen={isPopupOpen}
        toggle={togglePopup}
        onConfirm={handleClear}
      />
    </div>
  );
}